import React, { useState, useEffect, useCallback, useContext } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useIdleTimer } from 'react-idle-timer';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import ChannelsGrid from '@/components/ChannelsGrid/ChannelsGrid';
import PlayerSpinner from '@/icons/PlayerSpinner';
import Api from '@/services/Api';
import { ChannelContext } from '@/contexts/channel.context';
import { useTranslation } from 'next-i18next';
import { ViewListIcon, XIcon } from '@heroicons/react/outline';

const PlyrPlayer = dynamic(() => import('@/components/VideoPlayer/Plyr'), {
  ssr: false,
});

const ChannelPage: NextPage = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { setMainPlaylist, nowPlaying, setNowPlaying, mainPlaylist } =
    useContext(ChannelContext);

  const [loading, setLoading] = useState(true);
  const [showChannelGrid, setChannelGrid] = useState(true);
  const [channels, setChannels] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [videoKey, setVideoKey] = useState(0);

  useIdleTimer({
    timeout: 5000,
    onIdle: () => setChannelGrid(false),
    debounce: 500,
  });

  useEffect(() => {
    fetchChannels();
  }, []);

  // Add useEffect to monitor state changes
  useEffect(() => {
    console.log('State changed:', {
      nowPlaying,
      mainPlaylist,
      isUpdating,
    });
  }, [nowPlaying, mainPlaylist, isUpdating]);

  const fetchChannels = async () => {
    const channelList = await Api.get('v2/linear-channels', null, 'web_live');
    const selectedChannelId = router.query.selected;

    // Select the channel from the promotion banner
    if (selectedChannelId) {
      const channel = channelList.find(
        (item) => item._id === selectedChannelId,
      );
      setMainPlaylist(channel);
      setNowPlaying(channel.contents[0]);
    } else {
      setNowPlaying(channelList[0].contents[0]);
    }

    setChannels(channelList);
    setChannelGrid(true);
    setLoading(false);
  };

  const toggleGrid = useCallback(() => {
    setChannelGrid(!showChannelGrid);
  }, [showChannelGrid]);

  const handleVideoEnd = () => {
    // Prevent multiple calls while updating
    if (isUpdating) {
      console.log('Already updating, skipping...');
      return;
    }

    console.log('Video ended, current state:', {
      nowPlaying,
      mainPlaylist,
      currentVideoId: nowPlaying?._id,
    });

    if (!mainPlaylist?.contents || !nowPlaying) {
      console.log('Missing required data:', {
        hasMainPlaylist: !!mainPlaylist?.contents,
        hasNowPlaying: !!nowPlaying,
      });
      return;
    }

    setIsUpdating(true);

    const currentIndex = mainPlaylist.contents.findIndex(
      (item) => item._id === nowPlaying._id,
    );

    console.log('Current video index:', currentIndex);

    // If we're at the last video (index 51 in a 52-video playlist), loop back to the first one
    const nextIndex =
      currentIndex >= mainPlaylist.contents.length - 1 ? 0 : currentIndex + 1;

    console.log('Next video index:', nextIndex);

    const nextContent = mainPlaylist.contents[nextIndex];
    console.log('Next content to play:', {
      id: nextContent._id,
      title: nextContent.film_title,
      url: nextContent.finalvideoencodes?.final_url_20,
    });

    // Update both states together
    setMainPlaylist((prevPlaylist) => {
      const updatedPlaylist = {
        ...prevPlaylist,
        contents: prevPlaylist.contents.map((content, index) => ({
          ...content,
          isPlaying: index === nextIndex,
        })),
      };
      console.log('Updated playlist:', {
        currentPlayingIndex: nextIndex,
        totalContents: updatedPlaylist.contents.length,
      });
      return updatedPlaylist;
    });

    // Update nowPlaying and force video player re-render with the next index
    console.log('Updating nowPlaying state...');
    setNowPlaying(nextContent);
    setVideoKey(nextIndex);
    setIsUpdating(false);
  };

  return (
    <>
      <Head>
        <title>Channels | Butaca</title>
        <meta name="description" content="Channel" />
      </Head>
      <div className="container mx-auto mt-[6rem] mb-[4rem]">
        {loading ? (
          <div className="relative md:h-screen">
            <PlayerSpinner />
          </div>
        ) : (
          <div className="relative flex flex-col lg:flex-row w-full">
            {nowPlaying && (
              <PlyrPlayer
                key={`${videoKey}-${nowPlaying._id}`}
                isAutoplay
                poster={nowPlaying.bg?.url}
                content={nowPlaying}
                source={nowPlaying.finalvideoencodes?.final_url_20}
                onEnded={handleVideoEnd}
              />
            )}
            {showChannelGrid ? (
              <div className="w-full lg:absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent z-40">
                <div className="flex justify-between my-4 px-2 lg:px-0 lg:my-4">
                  <div className="flex items-center space-x-2 bg-black bg-opacity-40 rounded-md p-1 px-2 text-sm font-semibold">
                    {nowPlaying && (
                      <>
                        <span>{t('nowPlaying')}:</span>
                        <span> {nowPlaying.film_title}</span>
                      </>
                    )}
                  </div>
                  <button
                    className="bg-white bg-opacity-10 rounded-md p-1"
                    onClick={toggleGrid}
                  >
                    <XIcon className="w-6" />
                  </button>
                </div>
                <ChannelsGrid channels={channels} />
              </div>
            ) : (
              <div className="lg:absolute bottom-20 left-0 right-0 flex justify-center my-4 lg:my-0 lg:justify-end">
                <button
                  className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-1 px-2 text-sm"
                  onClick={toggleGrid}
                >
                  <span>{t('showChannels')}</span>
                  <ViewListIcon className="w-6" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <style global jsx>
        {`
          body::-webkit-scrollbar-thumb {
            display: none;
          }
        `}
      </style>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'profile'])),
    },
  };
};

export default ChannelPage;
